import dayjs from 'dayjs';
import { _ } from '/@/locale';
import { getSowingPhenologyForCrop, resolveWeedPhenologyMonths } from '/@/store/crop-protection/phenology';
import type { ApplicationOptionType, CropProtectionMonth, GetMonth, Option } from '/@/types/crop-protection';
import { getAllMonths, objectEntries } from '/@/utils';

const fallowGetMonths: (daysBeforeBig: number, daysBeforeSmall: number) => GetMonth =
    (daysBeforeBig, daysBeforeSmall) =>
    ({ createdDate, plantingDate, option, crop }) => {
        const sowing = getSowingPhenologyForCrop(crop);
        if (!plantingDate) {
            const monthsBeforeBig = daysBeforeBig / 30;
            return [
                {
                    disabled: true,
                },
                {
                    disabled: false,
                    columns: option.columns,
                },
                ...[...Array(Math.round(monthsBeforeBig - 1))].map((_) => ({
                    disabled: false,
                })),
                {
                    disabled: true,
                    icons: sowing.icons,
                    iconsDescriptions: sowing.iconsDescriptions,
                },
            ];
        }

        const planting = dayjs(plantingDate);
        const created = dayjs(createdDate);
        const allMonths = objectEntries(getAllMonths());
        const dateDaysBeforeBig = planting.subtract(daysBeforeBig, 'day');
        const dateDaysBeforeSmall = planting.subtract(daysBeforeSmall, 'day');

        // TODO: REFACTOR, NOT WORKING
        const months = [];
        let index = 0;
        let firstEnabledIndex;
        // Start at the earlier of the creation date and the window start, so the
        // application window is always represented. Otherwise, when the recommendation
        // is created after the fallow window has already passed, every iterated month
        // is disabled and the output's banner index math yields NaN grid columns.
        let monthDate = created.isBefore(dateDaysBeforeBig, 'month') ? created : dateDaysBeforeBig;

        while (planting.isAfter(monthDate, 'month') || planting.isSame(monthDate, 'month')) {
            let selectedColumns;
            let icons;

            // The 10 days when the date has the same month is for not active a month with minus than 10 days of overlap
            const enabled =
                ((monthDate.isSame(dateDaysBeforeBig, 'month') &&
                    dateDaysBeforeBig.date() <= dateDaysBeforeBig.daysInMonth() - 10) ||
                    monthDate.isAfter(dateDaysBeforeBig, 'month')) &&
                ((monthDate.isSame(dateDaysBeforeSmall, 'month') && dateDaysBeforeSmall.date() >= 10) ||
                    monthDate.isBefore(dateDaysBeforeSmall, 'month'));

            if (enabled && firstEnabledIndex === undefined) {
                firstEnabledIndex = index;
                selectedColumns = option.columns;
            }

            if (planting.isSame(monthDate, 'month')) {
                icons = sowing.icons;
            }

            months.push({
                disabled: !enabled,
                name: allMonths[monthDate.month()]?.[1],
                columns: selectedColumns,
                icons,
            });
            index++;
            monthDate = monthDate.add(1, 'month');
        }

        return months;
    };

const getPlagueMonths = (option: Option, applicationWindow?: string): Array<CropProtectionMonth> => {
    if (applicationWindow === 'v1_to_v8') {
        return [
            {
                disabled: true,
            },
            {
                disabled: false,
                columns: option.columns,
                icons: ['v1', 'v2', 'v3', 'v4', 'v5', 'v6', 'v7', 'v8'],
            },
            {
                disabled: true,
            },
        ];
    }
    if (applicationWindow === 'v2_to_v10') {
        return [
            {
                disabled: true,
            },
            {
                disabled: false,
                columns: option.columns,
                icons: ['v2', 'v3', 'v4', 'v5', 'v6', 'v7', 'v8', 'v9', 'v10'],
            },
            {
                disabled: true,
            },
        ];
    }
    return [
        {
            disabled: true,
        },
        {
            disabled: false,
            columns: option.columns,
        },
        {
            disabled: true,
        },
    ];
};

const isCornCrop = (crop?: string) => crop === 'corn';

export const getApplicationOptions: () => Record<string, ApplicationOptionType> = () => ({
    long_fallow: {
        title: () => _('Barbecho químico largo'),
        description: () => _('150-90 días antes de la siembra'),
        disabled: () => false,
        getMonths: (params) =>
            resolveWeedPhenologyMonths(params.crop, 'long_fallow', params.option, fallowGetMonths(150, 90)(params)),
    },
    short_fallow: {
        title: () => _('Barbecho químico corto'),
        description: () => _('60-20 días antes de la siembra'),
        disabled: () => false,
        getMonths: (params) =>
            resolveWeedPhenologyMonths(params.crop, 'short_fallow', params.option, fallowGetMonths(60, 20)(params)),
    },
    pre_emergent: {
        title: () => _('Pre-emergencia'),
        description: ({ type }) =>
            type === 'weeds'
                ? _('Etapa previa a la emergencia de la maleza.')
                : _('Etapa previa a la emergencia del cultivo.'),
        disabled: () => false,
        getMonths: ({ plantingDate, option, applicationWindow, crop }) => {
            if (option.type === 'plague') {
                return getPlagueMonths(option, applicationWindow);
            }
            const planting = dayjs(plantingDate);
            const allMonths = objectEntries(getAllMonths());
            return resolveWeedPhenologyMonths(crop, 'pre_emergent', option, [
                {
                    disabled: true,
                },
                {
                    disabled: false,
                    columns: option.columns,
                    icons: ['seed'],
                    iconsDescriptions: [_('Siembra')],
                    name: allMonths[planting.month()]?.[1],
                },
                {
                    disabled: true,
                    icons: ['emergence'],
                    iconsDescriptions: ['VE'],
                },
                {
                    disabled: true,
                    icons: ['v1'],
                    iconsDescriptions: ['V1'],
                },
                {
                    disabled: true,
                    icons: ['v2'],
                    iconsDescriptions: ['V2'],
                },
            ]);
        },
    },
    early_poe: {
        title: ({ crop } = {}) => (isCornCrop(crop) ? _('Post-emergencia temprana') : _('Post-emergencia')),
        description: () =>
            _('Etapa en la que la maleza ya emergió y el cultivo se encuentra en un estadio previo o igual a V2.'),
        disabled: () => false,
        getMonths: ({ plantingDate, option, crop }) => {
            const planting = dayjs(plantingDate);
            const allMonths = objectEntries(getAllMonths());
            return resolveWeedPhenologyMonths(crop, 'early_poe', option, [
                {
                    disabled: true,
                    icons: ['seed'],
                    name: allMonths[planting.month()]?.[1],
                    iconsDescriptions: [_('Siembra')],
                },
                {
                    disabled: true,
                    icons: ['emergence'],
                    iconsDescriptions: ['VE'],
                },
                {
                    disabled: false,
                    columns: option.columns,
                    icons: ['v1'],
                    iconsDescriptions: ['V1'],
                },
                {
                    disabled: false,
                    icons: ['v2'],
                    iconsDescriptions: ['V2'],
                },
                {
                    disabled: true,
                    icons: ['v3'],
                    iconsDescriptions: ['V3'],
                },
                {
                    disabled: true,
                    icons: ['v4'],
                    iconsDescriptions: ['V4'],
                },
            ]);
        },
    },
    late_poe: {
        title: ({ crop } = {}) => (isCornCrop(crop) ? _('Post-emergente tardía') : _('Post-emergente')),
        description: () => _('Etapa en la que el cultivo y la maleza ya han emergido.'),
        disabled: () => false,
        getMonths: ({ option, crop }) => {
            return resolveWeedPhenologyMonths(crop, 'late_poe', option, [
                {
                    disabled: false,
                    icons: ['emergence'],
                    iconsDescriptions: ['VE'],
                },
                {
                    disabled: false,
                    icons: ['v1'],
                    iconsDescriptions: ['V1'],
                },
                {
                    disabled: false,
                    columns: option.columns,
                    icons: ['v2'],
                    iconsDescriptions: ['V2'],
                },
                {
                    disabled: false,
                    icons: ['v3'],
                    iconsDescriptions: ['V3'],
                },
                {
                    disabled: false,
                    icons: ['v4'],
                    iconsDescriptions: ['V4'],
                },
                {
                    disabled: false,
                    icons: ['v5'],
                    iconsDescriptions: ['V5'],
                },
            ]);
        },
    },
    seed_treatment: {
        title: () => _('Tratamiento de semillas'),
        description: () => _('Protección de la semilla en la etapa previa a la siembra'),
        disabled: () => false,
        getMonths: ({ option, applicationWindow }) => getPlagueMonths(option, applicationWindow),
    },
    vegetative: {
        title: () => _('Periodo vegetativo'),
        description: () => _('El cultivo se encuentra en estado vegetativo'),
        disabled: () => false,
        getMonths: ({ option, applicationWindow }) => getPlagueMonths(option, applicationWindow),
    },
});

export const getCropOptions = () =>
    ({
        corn: { label: _('Maíz') },
        soy: { label: _('Soja') },
        wheat: { label: _('Trigo') },
        barley: { label: _('Cebada') },
    }) as const;

export const WEED_APPLICATION_MOMENT_CROPS = ['corn', 'soy', 'wheat', 'barley'] as const;
export const PLAGUE_CROPS = ['corn'] as const;

export const WEED_APPLICATION_MOMENTS_BY_CROP: Partial<
    Record<(typeof WEED_APPLICATION_MOMENT_CROPS)[number], string[]>
> = {
    // Soy has a single post-emergence moment (late_poe, title shows "Post-emergente").
    // early_poe is omitted — the backend emits late_poe for soy and both would show
    // near-identical labels; phenology.ts normalizes late_poe → early_poe builder.
    soy: ['long_fallow', 'short_fallow', 'pre_emergent', 'late_poe'],
    corn: ['long_fallow', 'short_fallow', 'pre_emergent', 'early_poe', 'late_poe'],
    wheat: ['pre_emergent', 'late_poe'],
    barley: ['pre_emergent', 'late_poe'],
};

export const SEED_TREATMENT_APPLICATION_MOMENT_CROPS = ['corn', 'soy', 'wheat', 'barley'] as const;

export const SEED_TREATMENT_APPLICATION_MOMENTS_BY_CROP: Partial<
    Record<(typeof SEED_TREATMENT_APPLICATION_MOMENT_CROPS)[number], string[]>
> = {
    corn: ['long_fallow', 'short_fallow', 'pre_emergent', 'early_poe', 'late_poe'],
    soy: ['long_fallow', 'short_fallow', 'early_poe', 'late_poe'],
    wheat: ['pre_emergent', 'early_poe', 'late_poe'],
    barley: ['pre_emergent', 'early_poe', 'late_poe'],
};

export const DISEASE_CROPS = ['corn', 'soy', 'wheat', 'barley'] as const;

export const getCropProtectionTypeOptions = (crop = 'corn') =>
    ({
        weeds: {
            value: 'malezas',
            title: _('Malezas'),
            subtitle: _('Herbicidas'),
            description: () => _('Encontrá el herbicida adecuado para un control eficiente de malezas.'),
            icon: 'weedRecommendation',
            disabled: () => false,
            disabledTooltip: _(
                'Este tipo de protección no está disponible actualmente. Pronto tendremos la recomendación disponible.'
            ),
        },
        plagues: {
            value: 'plagas',
            title: _('Plagas'),
            subtitle: _('Insecticidas'),
            description: () =>
                _('Encontrá el insecticida perfecto para un control eficiente de plagas en tus cultivos.'),
            icon: 'plagueRecommendation',
            disabled: () => !PLAGUE_CROPS.includes(crop as (typeof PLAGUE_CROPS)[number]),
            disabledTooltip: _(
                'Este tipo de protección no está disponible actualmente. Pronto tendremos la recomendación disponible.'
            ),
        },
        diseases: {
            value: 'enfermedades',
            title: _('Enfermedades'),
            subtitle: _('Fungicidas'),
            description: () =>
                _('Encontrá el fungicida ideal para un control efectivo de enfermedades en tus cultivos.'),
            icon: 'diseaseRecommendation',
            disabled: () => !DISEASE_CROPS.includes(crop as (typeof DISEASE_CROPS)[number]),
            disabledTooltip: _(
                'Este tipo de protección no está disponible actualmente. Pronto tendremos la recomendación disponible.'
            ),
        },
        seed_treatment: {
            value: 'tratamiento_de_semillas',
            title: _('Tratamiento de semillas'),
            subtitle: _('Tratamiento de semillas'),
            description: () => _('Protección de la semilla en la etapa previa a la siembra'),
            icon: 'seedBag',
            disabled: () => false,
            disabledTooltip: _(
                'Este tipo de protección no está disponible actualmente. Pronto tendremos la recomendación disponible.'
            ),
        },
    }) as const;

export const getApplicationTypeOptions = () =>
    ({
        preventive: { label: _('Preventiva') },
        healing: { label: _('Curativa') },
    }) as const;

export const getCropProtectionForm = (state) => state.cropProtectionForm;
